import {Inject, Injectable, PLATFORM_ID, Renderer2, RendererFactory2} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpEvent, HttpHeaders, HttpParams} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {
  Document,
  DocumentLinkCreate,
  DocumentValidation,
  DocumentValidationCreate,
  GetParams,
  GetResponse,
  PostResponse
} from '@app/shared';
import {Observable} from 'rxjs';
import {convertGetParamsToHttpParams} from '@app/core';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  /** URLs principales relacionadas con Documents **/
  documentUrl = environment.documetApiBaseUrl + '/api/document';
  documentValidationUrl =
    environment.documetApiBaseUrl + '/api/document-validation';
  documentTypeUrl = environment.documetApiBaseUrl + '/api/document-type';

  private renderer: Renderer2;

  constructor(
    private http: HttpClient,
    private rendererFactory: RendererFactory2,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  getDocument(getParams: any): Observable<GetResponse> {
    let params = new HttpParams()
      .set('data.startRow', getParams.startRow.toString())
      .set('data.endRow', getParams.endRow.toString())
      .set('data.sortDirection', getParams.sortDirection)
      .set('data.sortProperty', getParams.sortProperty)
      .set('entityId', getParams.entityId)
      .set('entityType', getParams.entityType);
    return this.http.get<GetResponse>(this.documentUrl, { params: params });
  }

  /**
   * Pide un documento por su id, sin pasar por la entidad a la que cuelga.
   *
   * `entityType=None` hace que el backend se salte el filtrado por entidad relacionada, que es lo
   * que permite alcanzar un documento de conductor o de equipo igual que uno de un servicio.
   *
   * El filtro por id NO es opcional: con `entityType=None` el backend fuerza `endRow` a 10000 y no
   * pagina, asi que una llamada sin filtro se traeria la tabla entera de documentos.
   */
  getDocumentById(id: number): Observable<GetResponse> {
    const params = new HttpParams()
      .set('data.startRow', '0')
      .set('data.endRow', '1')
      .set('data.sortDirection', 'asc')
      .set('data.sortProperty', 'id')
      .set('data.filterProperty', 'Id')
      .set('data.filterValue', id.toString())
      .set('entityId', '0')
      .set('entityType', 'None');
    return this.http.get<GetResponse>(this.documentUrl, { params });
  }

  createDocument(newDocument: FormData): Observable<HttpEvent<PostResponse>> {
    const headers = new HttpHeaders({ enctype: 'multipart/form-data' });
    return this.http.post<PostResponse>(this.documentUrl, newDocument, {
      headers,
      reportProgress: true,
      observe: 'events',
    });
  }

  updateDocument(document: Document): Observable<any> {
    return this.http.put(this.documentUrl, document);
  }

  patchDocument(value: any): Observable<any> {
    return this.http.patch(this.documentUrl, value);
  }

  deleteDocument(DocumentIds: number[]): Observable<any> {
    let params = new HttpParams();
    DocumentIds.forEach((id) => {
      params = params.append('ids', id.toString()); // Agrega cada ID como un parámetro "ids"
    });
    return this.http.delete(this.documentUrl, { params });
  }

  createDocumentLink(newDocument: DocumentLinkCreate): Observable<PostResponse> {

    const body = {
      documentId: newDocument.documentId,
      innerLinkDocumentPayload: {
        entityId: newDocument.innerLinkDocumentPayload.entityId,
        isLinked: newDocument.innerLinkDocumentPayload.isLinked,
      },
      entityType: newDocument.entityType,
    }
    // Construye los query params desde el objeto newDocument
    // const params = new HttpParams()
    //   .set('documentId', newDocument.documentId.toString())
    //   .set('innerLinkDocumentPayload.entityId', newDocument.innerLinkDocumentPayload.entityId.toString())
    //   .set('innerLinkDocumentPayload.isLinked', newDocument.innerLinkDocumentPayload.isLinked.toString())
    //   .set('entityType', newDocument.entityType);

    // Envía los parámetros en la URL y un cuerpo vacío (o elimina el cuerpo si el backend lo requiere)
    return this.http.post<PostResponse>(
      this.documentUrl + '/link',
      body, // Cuerpo vacío (o null si es necesario)
    );
  }

  /** Logica de DocumentValidation  **/
  getDocumentValidation(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.documentValidationUrl, {
      params: convertGetParamsToHttpParams(getParams),
    });
  }

  createDocumentValidation(
    newDocumentValidation: DocumentValidationCreate
  ): Observable<PostResponse> {
    return this.http.post<PostResponse>(
      this.documentValidationUrl,
      newDocumentValidation
    );
  }

  updateDocumentValidation(
    documentValidation: DocumentValidation
  ): Observable<any> {
    return this.http.put(this.documentValidationUrl, documentValidation);
  }

  deleteDocumentValidation(DocumentValidationIds: number[]): Observable<any> {
    return this.http.delete(this.documentValidationUrl, {
      body: { ids: DocumentValidationIds },
    });
  }

  /** Document Type **/
  getDocumentType(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.documentTypeUrl, {
      params: convertGetParamsToHttpParams(getParams),
    });
  }

  createDocumentType(documentType: any): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.documentTypeUrl, documentType);
  }

  updateDocumentType(documentType: any): Observable<any> {
    return this.http.put(this.documentTypeUrl, documentType);
  }

  deleteDocumentType(ids: number[]): Observable<any> {
    return this.http.delete(this.documentTypeUrl,{ params: { ids } });
  }

  /** Logica de análisis IA **/
  uploadForAiAnalysis(formData: FormData): Observable<HttpEvent<PostResponse>> {
    const headers = new HttpHeaders({ enctype: 'multipart/form-data' });
    return this.http.post<PostResponse>(
      this.documentUrl + '/ai/extract-mbl',
      formData,
      { headers, reportProgress: true, observe: 'events' }
    );
  }

  uploadForAiAnalysisBatchServiceOrders(formData: FormData): Observable<HttpEvent<PostResponse>> {
    const headers = new HttpHeaders({ enctype: 'multipart/form-data' });
    return this.http.post<PostResponse>(
      this.documentUrl + '/ai/extract-mbl/batch-service-orders',
      formData,
      { headers, reportProgress: true, observe: 'events' }
    );
  }

  /** Logica para descargar archivos **/
  openDocumentInNewTab(document: Document): void {
    if (document?.url) {
      window.open(document.url, '_blank');
    } else {
      console.error('URL no válida');
    }
  }

  private isValidDocument(document: Document): boolean {
    return !!document?.url && document.url.startsWith('http');
  }

  private prepareDownloadData(document: Document): {
    url: string;
    fileName: string;
  } {
    return {
      url: document.url,
      fileName: document.name?.trim() || `documento_${new Date().getTime()}`,
    };
  }

  private isDirectFileLink(url: string): boolean {
    const fileExtensions = [
      'pdf',
      'doc',
      'docx',
      'xls',
      'xlsx',
      'jpg',
      'png',
      'txt',
    ];
    const extension = url.split('.').pop()?.toLowerCase();
    return !!extension && fileExtensions.includes(extension);
  }

  private setupDirectDownload(
    link: HTMLAnchorElement,
    url: string,
    fileName: string
  ): void {
    this.renderer.setAttribute(link, 'href', url);
    this.renderer.setAttribute(link, 'target', '_blank');
    this.renderer.setAttribute(link, 'download', fileName);
    this.renderer.setAttribute(link, 'rel', 'noopener noreferrer');
  }

  private async handleBlobDownload(
    link: HTMLAnchorElement,
    url: string,
    fileName: string
  ): Promise<void> {
    try {
      const blob = await this.http
        .get(url, { responseType: 'blob' })
        .toPromise();
      const blobUrl = window.URL.createObjectURL(blob!);

      this.renderer.setAttribute(link, 'href', blobUrl);
      this.renderer.setAttribute(link, 'target', '_blank');
      this.renderer.setAttribute(link, 'download', fileName);

      // Limpieza automática después de 1 minuto como respaldo
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      throw new Error(
        `Error al obtener el archivo: ${this.getErrorMessage(error)}`
      );
    }
  }

  private executeDownload(link: HTMLAnchorElement): void {
    this.renderer.setStyle(link, 'display', 'none');
    this.renderer.appendChild(document.body, link);
    link.click();
    this.renderer.removeChild(document.body, link);
  }

  private handleDownloadError(error: unknown): void {
    const errorMessage =
      error instanceof Error ? error.message : 'Error desconocido';
    this.handleError(`Fallo en la descarga: ${errorMessage}`);
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return `Error del servidor (${error.status}): ${error.statusText}`;
    }
    return error instanceof Error ? error.message : 'Error desconocido';
  }

  private handleError(message: string): void {
    // Implementa tu lógica de notificación aquí
    console.error(message);
    alert(message);
  }
}
